import type { FC, ReactElement } from "react"
import { useMemo } from "react"

import isUndefined from "lodash-es/isUndefined"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Link,
    RsvpMark,
    Typography
} from "@/3514/components"
import { ESessionInvitationStatus } from "@/3514/types"
import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { CoachConferenceAccountName as Name } from "./coach.conference-account-name.component"
import { coachConferenceAccountConfig as config } from "./coach.conference-account.config"
import type {
    TCoachConferenceAccount as TProps,
    TCoachConferenceAccountRenderConfig as TRenderConfig
} from "./coach.conference-account.types"

const { renderConfig } = config

const classes = {
    card: buildCssClassName(
        "px-[20px]",
        "pt-[20px]",
        "pb-[25px]",
        "flex",
        "flex-col",
        "gap-y-[10px]",
        "items-center",
        "max-w-[340px]",
        "h-[235px]",
        "w-full",
        "bg-blue-600",
        "rounded-[20px]",
        getTransitionTwClasses("all", 100)
    ),
    description: "max-w-[260px] w-full select-none selection:bg-blue-400",
    statusWrapper: buildCssClassName(
        "flex",
        "gap-x-[10px]",
        "items-center",
        "py-[12px]",
        "px-[15px]",
        "border-[1px]",
        "border-blue-500",
        "bg-white",
        "mt-auto",
        "w-full",
        "rounded-[10px]"
    ),
    statusIconWrapper: "flex gap-x-[5px] items-center select-none",
    status: "max-w-[170px] truncate"
}

/**
 * Coach' conferencing account card
 * Design: (Zoom: {@link https://www.figma.com/design/QLhX4KcIViCdMD12xdfvXq/Coach?node-id=3084-48163&m=dev})
 * Design: (MS Teams: {@link https://www.figma.com/design/QLhX4KcIViCdMD12xdfvXq/Coach?node-id=3084-48172&m=dev})
 */
const CoachConferenceAccount: FC<TProps> = ({ variant, email, linkUrl }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const configToRender: TRenderConfig = useMemo(
        (): TRenderConfig => renderConfig({ t, variant, linkUrl }),
        [t, variant, linkUrl]
    )

    return (
        <article className={classes.card}>
            <Name variant={variant} titleSize={ETypographySize.Heading2} titleTag={ETypographyTag.Heading2} />

            <Typography
                text={configToRender.description}
                tag={ETypographyTag.Paragraph}
                color={ETypographyColor.Dark}
                alignment={ETypographyAlignment.Center}
                size={ETypographySize.Small}
                className={classes.description}
            />

            {isUndefined(configToRender.actionProps) ? (
                <div className={classes.statusWrapper}>
                    <Typography
                        text={email}
                        tag={ETypographyTag.Paragraph}
                        color={ETypographyColor.Dark}
                        className={classes.status}
                    />

                    <div className={classes.statusIconWrapper}>
                        <Typography
                            text={t("common.success")}
                            tag={ETypographyTag.Paragraph}
                            color={ETypographyColor.Black}
                            weight={ETypographyWeight.Bold}
                        />
                        <RsvpMark status={ESessionInvitationStatus.Present} />
                    </div>
                </div>
            ) : (
                <Link {...configToRender.actionProps} />
            )}
        </article>
    )
}

CoachConferenceAccount.displayName = "CoachConferenceAccount"

export { CoachConferenceAccount }
